import { ITouchpoint, IViewpoint, IMediaResponse } from "../../../../types";
import { DisplayBoxBase, TagChip } from "../../../../components/shared/StyledComponents";
import { AdvisorProfileSummary } from "../../../advisor/components/AdvisorProfileSummary";
import { Box, Typography } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { VideoJS } from "../../../../components/VideoJs";
import useAnalytics from "../../../../hooks/useAnalytics";

export const InsightResponses = ({ touchpoint }: { touchpoint: ITouchpoint }) => {
    const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
    const viewpoints = touchpoint.viewpoints || [];
    const { trackEvent } = useAnalytics();
    if (!viewpoints || viewpoints.length === 0) {
        return <Typography>No responses yet.</Typography>;
    }

    const handleTopicClick = (topic: string) => {
        setSelectedTopics(prev =>
            prev.includes(topic)
                ? prev.filter(t => t !== topic)
                : [...prev, topic]
        );
    };

    const sortedViewpoints = viewpoints.sort((a, b) => {
        // Sort viewpoints with video first, then those without video
        const aHasVideo = a.media_responses?.some(response => response.type === "video");
        const bHasVideo = b.media_responses?.some(response => response.type === "video");
        
        // First, sort by video presence
        if (aHasVideo && !bHasVideo) return -1;
        if (!aHasVideo && bHasVideo) return 1;
        
        // Within each group (with/without video), sort by featured status
        const aIsFeatured = a.is_featured === true;
        const bIsFeatured = b.is_featured === true;
        
        if (aIsFeatured && !bIsFeatured) return -1;
        if (!aIsFeatured && bIsFeatured) return 1;
        
        return 0;
    });

    const filteredViewpoints = selectedTopics.length === 0
        ? sortedViewpoints
        : sortedViewpoints.filter(viewpoint => {
            const viewpointTopics = touchpoint.media_analysis_topic_to_media_map
                ? Object.entries(touchpoint.media_analysis_topic_to_media_map)
                    .filter(([topic]) => selectedTopics.includes(topic))
                    .flatMap(([, mediaIds]) => mediaIds)
                : [];

            return viewpoint.media_responses?.some(response =>
                viewpointTopics.includes(response.id)
            );
        });

    const handleVideoPlay = (videoUrl: string) => {
        trackEvent("Watched advisor video", {
            project: touchpoint.project.tag,
            organization: touchpoint.project.organization.tag,
            touchpoint_uuid: touchpoint.uuid,
            touchpoint_name: touchpoint.name,
            video_url: videoUrl,
        });
    }

    return (
        <Box mb="40px">
            <Typography fontWeight={600} sx={{ mb: 1 }}>Explore what advisors say about:</Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {touchpoint.media_analysis_topics && touchpoint.media_analysis_topics.map((topic) => (
                    <TagChip
                        key={topic}
                        variant={selectedTopics.includes(topic) ? "filled" : "outlined"}
                        label={`${topic} (${touchpoint.media_analysis_topic_to_media_map?.[topic]?.length})`}
                        onClick={() => handleTopicClick(topic)}
                        sx={{ cursor: 'pointer' }}
                    />
                ))}
            </Box>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 3,
                mb: "40px"
            }}>
                {filteredViewpoints.map((viewpoint: IViewpoint) => {

                    const videoUrl = viewpoint.media_responses?.find((response: IMediaResponse) => response.type === "video")?.video_url;

                    return (
                        <DisplayBoxBase key={viewpoint.id} className="white column" sx={{ alignItems: 'flex-start', padding: 2, boxSizing: 'border-box' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {viewpoint.advisor && <AdvisorProfileSummary advisor={viewpoint.advisor} showCompany={true} />}
                            </Box>
                            <Box sx={{ flex: 1, width: '100%', mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {videoUrl && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, width: '100%', aspectRatio: '16/9' }}>
                                        <VideoJS 
                                            src={videoUrl}
                                            onPlay={() => handleVideoPlay(videoUrl)}
                                        />
                                    </Box>
                                )}
                                {touchpoint.media_analysis_highlights && (
                                    <Box sx={{ mt: 1 }}>
                                        {touchpoint.media_analysis_highlights
                                            .filter(highlight =>
                                                viewpoint.media_responses?.some(response => response.id === highlight.mediaResponseId)
                                            )
                                            .map((highlight, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    sx={{
                                                        fontStyle: 'italic',
                                                        color: '#222',
                                                    }}
                                                >
                                                    "{highlight.quote}"
                                                </Typography>
                                            ))}
                                    </Box>
                                )}
                            </Box>
                        </DisplayBoxBase>
                    )
                })}
            </Box>
        </Box>
    );
};